/**
 * ehr#743 (PROFILE_TABS_SEARCH_10-30-V15): the Profile tab's section tabs, Mock B. A white
 * bordered strip with short dividers between the tabs; the open tab is a solid green pill with
 * white text and the dividers beside it hide. Not a wizard: no numbers, checks, progress line or
 * Next, and the tabs open in any order. An amber dot after a label means something on that section
 * is missing.
 *
 * More, at the end, holds Pharmacy and Employment. It shows the dot when a tab inside needs info,
 * and while one of them is open it becomes the green pill named for it ("Employment"). It is a
 * menu button: Down (or Enter) opens it, Escape, a click outside or focus leaving closes it, and
 * its items are menuitemradio with aria-checked. Arrows move along the row. Below about 900px of
 * chart width the strip becomes a native dropdown in the same order (styles.css, .pst).
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { arrowMove, moreLabel, type ProfileTab } from '../lib/profileTabs.js';

export type SectionTab = ProfileTab & { need: boolean };

const MENU_ID = 'pst-more-menu';

export function Chevron() {
  return (
    <svg
      className="pst-chev"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

const needLabel = (t: SectionTab) => (t.need ? `${t.label}, needs info` : t.label);

export function ProfileSectionTabs({
  row,
  more,
  current,
  onPick,
  initialMoreOpen = false,
}: {
  row: readonly SectionTab[];
  more: readonly SectionTab[];
  /** The open section's key. */
  current: string;
  onPick: (key: string) => void;
  /** For tests (server render): start with the More list open. */
  initialMoreOpen?: boolean;
}) {
  const [open, setOpen] = useState(initialMoreOpen);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const inMore = more.some((t) => t.key === current);
  const moreNeed = more.some((t) => t.need);
  const rovingKey = row.some((t) => t.key === current) || inMore ? current : row[0]?.key;

  // A click anywhere outside, or focus moving outside (V15 fix 7), closes the More list.
  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('focusin', outside);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('focusin', outside);
    };
  }, [open]);

  const pick = (key: string) => {
    setOpen(false);
    onPick(key);
  };
  const focusMenuItem = (i: number) => window.setTimeout(() => itemRefs.current[i]?.focus(), 0);
  // The row's focus order: its tabs, then More.
  const focusRow = (i: number) => {
    if (i < row.length) rowRefs.current[i]?.focus();
    else btnRef.current?.focus();
  };
  const rowCount = row.length + (more.length ? 1 : 0);

  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const to = arrowMove(e.key, i, rowCount);
    if (to == null) return;
    e.preventDefault();
    focusRow(to);
  };
  const onMoreKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      focusMenuItem(e.key === 'ArrowDown' ? 0 : more.length - 1);
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      focusRow(arrowMove(e.key, row.length, rowCount) ?? 0);
    }
  };
  const onItemKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const to = arrowMove(e.key, i, more.length);
    if (to == null) return;
    e.preventDefault();
    itemRefs.current[to]?.focus();
  };

  return (
    <div className="pst">
      <div className="pst-strip" role="tablist" aria-label="Profile sections">
        {row.map((t, i) => {
          const on = t.key === current;
          return (
            <button
              key={t.key}
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`pst-tab-${t.key}`}
              aria-selected={on}
              aria-label={needLabel(t)}
              tabIndex={t.key === rovingKey ? 0 : -1}
              className={`pst-tab${on ? ' on' : ''}${t.need ? ' need' : ''}`}
              onClick={() => pick(t.key)}
              onKeyDown={(e) => onTabKey(e, i)}
            >
              <span className="pst-l">{t.label}</span>
              {t.need && <span className="pst-dot" aria-hidden />}
            </button>
          );
        })}
        {more.length > 0 && (
          <div
            className={`pst-more-wrap${inMore ? ' on' : ''}`}
            ref={wrapRef}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && open) {
                e.stopPropagation();
                setOpen(false);
                btnRef.current?.focus();
              }
            }}
            onBlur={(e) => {
              if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
            }}
          >
            <button
              ref={btnRef}
              type="button"
              className={`pst-more${inMore ? ' on' : ''}${moreNeed ? ' need' : ''}`}
              aria-haspopup="menu"
              aria-controls={MENU_ID}
              aria-expanded={open}
              aria-label={`${inMore ? `${moreLabel(more, current)}, more sections` : 'More sections'}${moreNeed ? ', needs info' : ''}`}
              tabIndex={inMore ? 0 : -1}
              onClick={(e) => {
                const next = !open;
                setOpen(next);
                // Opened from the keyboard (Enter / Space): the first item takes focus.
                if (next && e.detail === 0) focusMenuItem(0);
              }}
              onKeyDown={onMoreKey}
            >
              <span className="pst-l">{moreLabel(more, current)}</span>
              <Chevron />
              {moreNeed && <span className="pst-dot" aria-hidden />}
            </button>
            <div className={`pst-menu${open ? ' open' : ''}`} id={MENU_ID} role="menu">
              {more.map((t, i) => {
                const on = t.key === current;
                return (
                  <button
                    key={t.key}
                    ref={(el) => {
                      itemRefs.current[i] = el;
                    }}
                    type="button"
                    role="menuitemradio"
                    aria-checked={on}
                    aria-label={needLabel(t)}
                    tabIndex={-1}
                    className={`pst-item${on ? ' on' : ''}${t.need ? ' need' : ''}`}
                    onClick={() => {
                      pick(t.key);
                      btnRef.current?.focus();
                    }}
                    onKeyDown={(e) => onItemKey(e, i)}
                  >
                    <span className="pst-l">{t.label}</span>
                    {t.need && <span className="pst-dot" aria-hidden />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {/* Narrow charts (phones, tablets): a native dropdown, same order, "(needs info)" spelled out. */}
      <select
        className="pst-select"
        aria-label="Profile section"
        value={current}
        onChange={(e) => pick(e.target.value)}
      >
        {[...row, ...more].map((t) => (
          <option key={t.key} value={t.key}>
            {t.need ? `${t.label} (needs info)` : t.label}
          </option>
        ))}
      </select>
    </div>
  );
}
